import { Global, css } from '@emotion/react';
import styled from '@emotion/styled';
import { Sidebar } from 'primereact/sidebar';
import React from 'react';
import { Z_INDEX } from '@/components/shared/constants/zIndex';

const sideDrawerVarsCss = css`
    :root {
        --side-drawer-bg: #ffffff;
        --side-drawer-divider: rgba(10, 30, 58, 0.06);
        --side-drawer-scroll-hint-border: rgba(10, 30, 58, 0.12);
        --side-drawer-scroll-hint-text: #0a1e3a;
        --side-drawer-scroll-hint-hover: #f5f8fd;
        --side-drawer-push-right: 0px;
        --side-drawer-push-left: 0px;
    }

    /* Squeeze the page content away from a non-modal drawer instead of letting
       the drawer cover the underlying UI. Drawers that opt into this set the
       --side-drawer-push-{left|right} CSS variable to their width while open;
       the drawer itself is position:fixed so it stays anchored to the viewport. */
    body {
        padding-right: var(--side-drawer-push-right);
        padding-left: var(--side-drawer-push-left);

        @media (prefers-reduced-motion: no-preference) {
            transition:
                padding-right 200ms ease,
                padding-left 200ms ease;
        }
    }

    /* Extend the 64px app header into the gap above the drawer so the navy bar
       runs the full viewport width even when the body is squeezed. ::before /
       ::after sit in fixed-position layers so they're not affected by the body
       padding. They stay 0px-wide when the corresponding push variable is 0,
       so non-pushing drawers (and no-drawer state) render nothing. */
    body::before,
    body::after {
        content: '';
        position: fixed;
        top: 0;
        height: 64px;
        background-color: #00263e;
        pointer-events: none;
        z-index: ${Z_INDEX.SIDE_DRAWER_BODY_EXTENSION};

        @media (prefers-reduced-motion: no-preference) {
            transition: width 200ms ease;
        }
    }
    body::before {
        left: 0;
        width: var(--side-drawer-push-left);
    }
    body::after {
        right: 0;
        width: var(--side-drawer-push-right);
    }

    /* Raise the top app header above the Sidebar mask so the blue bar stays
       fully visible (no dim overlay) while a drawer is open. */
    [data-testid='header-container'] {
        position: relative;
        z-index: ${Z_INDEX.HEADER};
    }

    /* The design-system Modal renders its overlay at z-index 1000, but the
       PrimeReact Sidebar uses an auto-incrementing z-index starting around
       1100 — so a Modal opened *while* a Sidebar is open ends up underneath
       the drawer. Only when a Sidebar is currently visible, lift any DS
       Modal (id="background-container") above the drawer's layer. Scoped via
       :has() so standalone Modals (no drawer) keep their original z-index of
       1000. Stays below our 10000 header so the navy bar still wins. */
    body:has(.p-sidebar-visible) #background-container {
        z-index: ${Z_INDEX.MODAL_ABOVE_DRAWER} !important;
    }

    /* Anchor any Sidebar mask flush below the 64px header. */
    .p-sidebar-mask.p-component-overlay:has(.p-sidebar-left),
    .p-sidebar-mask.p-component-overlay:has(.p-sidebar-right) {
        top: 64px !important;
        bottom: 0 !important;
        height: auto !important;
        align-items: stretch !important;
    }

    .p-sidebar.p-sidebar-left,
    .p-sidebar.p-sidebar-right {
        position: fixed !important;
        top: 64px !important;
        bottom: 0 !important;
        height: auto !important;
        max-height: none !important;
        margin: 0 !important;
    }

    .p-sidebar.p-sidebar-left {
        left: 0 !important;
    }

    .p-sidebar.p-sidebar-right {
        right: 0 !important;
    }

    .p-sidebar-mask.p-component-overlay {
        position: fixed !important;
    }
`;

export const SideDrawerGlobalStyles: React.FC = () =>
    React.createElement(Global, { styles: sideDrawerVarsCss });

export const StyledSidebar = styled(Sidebar)`
    top: 64px !important;
    bottom: 0 !important;
    height: auto !important;
    max-height: none !important;
    margin: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    box-sizing: border-box !important;

    .p-sidebar-header {
        padding: 1.5rem 1.25rem 1rem;
        flex-shrink: 0;
    }

    .p-sidebar-content {
        flex: 1 1 auto;
        min-height: 0;
        display: flex;
        flex-direction: column;
        padding: 0;
        overflow: hidden;
    }
`;

export const HeaderTitle = styled.span`
    font-size: 18px;
    font-weight: 600;
    color: #0a1e3a;
`;

export const BodyWrapper = styled.div`
    position: relative;
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
`;

const BODY_GAP_PX = 16;

export const Body = styled.div`
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: ${BODY_GAP_PX}px;
    padding: 0 1.25rem 1rem;
`;

export const EndSentinel = styled.div`
    height: 1px;
    margin-top: -${BODY_GAP_PX}px;
    flex-shrink: 0;
`;

export const ScrollHintFade = styled.div<{ visible: boolean }>`
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 56px;
    pointer-events: none;
    background: linear-gradient(to bottom, transparent 0%, var(--side-drawer-bg) 85%);
    opacity: ${({ visible }) => (visible ? 1 : 0)};

    @media (prefers-reduced-motion: no-preference) {
        transition: opacity 150ms ease;
    }
`;

export const ScrollHintChevron = styled.button<{ visible: boolean }>`
    position: absolute;
    left: 50%;
    bottom: 8px;
    z-index: 1;
    transform: translateX(-50%);
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 1px solid var(--side-drawer-scroll-hint-border);
    background: var(--side-drawer-bg);
    box-shadow: 0 1px 3px var(--side-drawer-scroll-hint-border);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--side-drawer-scroll-hint-text);
    cursor: pointer;
    opacity: ${({ visible }) => (visible ? 1 : 0)};
    pointer-events: ${({ visible }) => (visible ? 'auto' : 'none')};

    @media (prefers-reduced-motion: no-preference) {
        transition:
            opacity 150ms ease,
            background 120ms ease;
    }

    &:hover {
        background: var(--side-drawer-scroll-hint-hover);
    }
`;

export const Footer = styled.div`
    flex-shrink: 0;
    display: flex;
    gap: 8px;
    padding: 16px 1rem;
    border-top: 1px solid var(--side-drawer-divider);
    background: #ffffff;

    & > button {
        flex: 1 1 0;
    }
`;
